import React from 'react';

import clsx from 'classnames';
import PropTypes from 'prop-types';

import {
    capitalize,
    combineClasses,
} from 'wanderu-css';

import GridContext from '../Grid/GridContext';

import styles from './GridItem.scss';

function GridItem(props) {
    const {
        align: alignProp = undefined, // this must be undefined or it overrides the value in the context
        classes: classesProp = {},
        className,
        component: Component = 'span',
        componentProps,
        justify: justifyProp = undefined, // this must be undefined or it overrides the value in the context
        xs,
        sm,
        md,
        lg,
        xl,
        ...otherProps
    } = props;

    const {
        align: alignContext,
        justify: justifyContext,
    } = React.useContext(GridContext);

    const align = alignProp || alignContext || 'center';
    const justify = justifyProp || justifyContext || 'center';

    const classes = combineClasses(styles, classesProp);

    return (
        <Component
            className={clsx(
                classes.root,
                {
                    [classes[`align${capitalize(align)}`]]: align !== 'center',
                    [classes[`justify${capitalize(justify)}`]]: justify !== 'center',
                    [classes[`grid-item-xs-${xs}`]]: xs !== false,
                    [classes[`grid-item-sm-${sm}`]]: sm !== false,
                    [classes[`grid-item-md-${md}`]]: md !== false,
                    [classes[`grid-item-lg-${lg}`]]: lg !== false,
                    [classes[`grid-item-xl-${xl}`]]: xl !== false,
                },
                className,
            )}
            {...otherProps}
            {...componentProps}
        />
    );
}

GridItem.propTypes = {
    /**
     * Set the alignment on the component.
     */
    align: PropTypes.oneOf(['start', 'center', 'end']),
    /**
     * The content of the component.
     */
    children: PropTypes.node.isRequired,
    /**
     * Override or extend the styles applied to the component.
     */
    classes: PropTypes.object,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * The component used for the root node.
     * Either a string to use a HTML element or a component.
     */
    component: PropTypes.elementType,
    /**
     * The set of components passed down to the root component
     */
    componentProps: PropTypes.object,
    /**
     * Set the justification on the component.
     */
    justify: PropTypes.oneOf(['start', 'center', 'end']),
    /**
     * Defines the number of column the component is going to span. It's applied for the xs breakpoint and wider screens if not overridden.
     */
    xs: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    /**
     * Defines the number of column the component is going to span. It's applied for the sm breakpoint and wider screens if not overridden.
     */
    sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    /**
     * Defines the number of column the component is going to span. It's applied for the md breakpoint and wider screens if not overridden.
     */
    md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    /**
     * Defines the number of column the component is going to span. It's applied for the lg breakpoint and wider screens if not overridden.
     */
    lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    /**
     * Defines the number of column the component is going to span. It's applied for the xl breakpoint and wider screens if not overridden.
     */
    xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
};

export default GridItem;
